import * as Sentry from "@sentry/react";

import { setSentryInitialized } from "./logger";

/**
 * Initialize Sentry SDK with proper configuration for both development and production monitoring
 * This should be called before React app renders (in index.tsx)
 */
export const initSentry = () => {
  try {
    // Use environment variable if available, otherwise use provided DSN
    const dsn = process.env.REACT_APP_SENTRY_DSN;
    const environment =
      process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development";
    const isProduction = environment === "production";

    // Release version for source map matching
    // Should match the release version used during build (in config-overrides.js)
    // Set REACT_APP_SENTRY_RELEASE at build time to match the release used for source map upload
    const release = process.env.REACT_APP_SENTRY_RELEASE;

    // Always initialize Sentry for both development and production monitoring
    Sentry.init({
      dsn,
      // Release version for source map matching
      // This must match the release version used when uploading source maps during build
      release,
      // Setting this option to true will send default PII data to Sentry.
      // For example, automatic IP address collection on events
      sendDefaultPii: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Tracing - Capture 100% in development, 10% in production (to reduce overhead)
      tracesSampleRate: isProduction ? 0.1 : 1.0,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      // Empty array disables trace header propagation (prevents CORS issues with backends that don't support it)
      // For production, only enable if backend supports Sentry distributed tracing
      tracePropagationTargets: isProduction
        ? ([
            // Only enable trace propagation for production endpoints that support it
            /^https:\/\/.*\.netskope\.com\/api/,
            process.env.REACT_APP_BASE_URL && !process.env.REACT_APP_BASE_URL.includes("localhost")
              ? new RegExp(`^${process.env.REACT_APP_BASE_URL}/api`)
              : undefined,
          ].filter(Boolean) as (string | RegExp)[])
        : [], // Disable trace propagation in development to avoid CORS issues
      // Session Replay
      replaysSessionSampleRate: isProduction ? 0.1 : 1.0, // 10% in production, 100% in development
      replaysOnErrorSampleRate: 1.0, // Always capture 100% of sessions where errors occur (both dev and prod)
      // Enable logs to be sent to Sentry
      enableLogs: true,
      environment,
      // Production-specific settings
      ...(isProduction && {
        // In production, reduce noise by filtering out some console errors
        ignoreErrors: [
          // Browser extensions
          "top.GLOBALS",
          "originalCreateNotification",
          "canvas.contentDocument",
          "MyApp_RemoveAllHighlights",
          "atomicFindClose",
          // Facebook plugin
          "fb_xd_fragment",
          // Miscellaneous
          "bmi_SafeAddOnload",
          "EBCallBackMessageReceived",
          "conduitPage",
          // MetaMask and Web3 wallet extensions
          "metamask",
          "wallet",
          "ethereum",
          "web3",
          "failed to connect to metamask",
          "chrome-extension://",
          "moz-extension://",
        ],
        // Only send errors that occur frequently enough
        maxBreadcrumbs: 50,
      }),
      beforeSend(event, _hint) {
        // Filter sensitive data before sending to Sentry (both dev and prod)
        if (event.request) {
          delete event.request.cookies;
          if (event.request.headers) {
            delete event.request.headers["Authorization"];
            delete event.request.headers["authorization"];
          }
        }

        // Filter out browser extension errors (MetaMask, etc.) - both dev and prod
        const errorMessage = event.exception?.values?.[0]?.value?.toLowerCase() || "";
        const errorType = event.exception?.values?.[0]?.type?.toLowerCase() || "";
        const combinedError = `${errorMessage} ${errorType}`;

        const extensionPatterns = [
          "metamask",
          "wallet",
          "ethereum",
          "web3",
          "failed to connect to metamask",
          "chrome-extension://",
          "moz-extension://",
          "safari-extension://",
          "extension://",
        ];

        if (extensionPatterns.some((pattern) => combinedError.includes(pattern))) {
          // Don't send browser extension errors to Sentry
          return null;
        }

        // In production, you may want to filter out certain error types
        if (isProduction) {
          // Example: Filter out network errors if they're too noisy
          // if (event.exception?.values?.[0]?.type === 'NetworkError') {
          //   return null; // Don't send this event
          // }
        }

        return event;
      },
    });

    // Mark Sentry as initialized
    setSentryInitialized(true);
  } catch (error) {
    // Don't break the app if Sentry initialization fails
     
    if (process.env.NODE_ENV === "development") {
      console.warn("Failed to initialize Sentry:", error);
    }
    setSentryInitialized(false);
  }
};
